import { useNavigate } from "react-router-dom";

function PrivacyPolicy() {
  const navigate = useNavigate();
  const handleContactSales = () => navigate("/contact");

  return (
    <div>
      {/* Hero section */}
      <section className="hero-section">
        <div className="hero-content">
          <h1>Privacy Policy</h1>
          <p>
            AirArrange is committed to protecting your privacy and handling
            personal information in a transparent and secure manner. This
            Privacy Policy explains how we collect, use, disclose and protect
            personal information in accordance with the Privacy Act 1988 (Cth)
            and the Australian Privacy Principles.
          </p>
        </div>
      </section>

      {/* Content */}
      <section className="max-w-5xl mx-auto px-6 py-16 space-y-12">
        {/* Summary */}
        <div className="bg-gray-50 border rounded-lg p-6">
          <h2 className="text-xl font-semibold mb-3">Privacy at a Glance</h2>
          <ul className="list-disc pl-6 text-gray-600 space-y-2">
            <li>
              We only collect information necessary to operate the platform
            </li>
            <li>
              Payments are processed securely by Stripe — we never store card
              details
            </li>
            <li>
              Identity verification for certain users is facilitated through
              Stripe
            </li>
            <li>Users can message each other securely within the platform</li>
            <li>
              You can access or correct your personal information at any time
            </li>
          </ul>
        </div>

        {/* Platform role */}
        <div>
          <h2 className="text-2xl font-semibold mb-4">
            Our Role as a Platform
          </h2>
          <p className="text-gray-600">
            AirArrange operates a digital marketplace that enables users to
            discover and book workspaces offered by third‑party providers. We
            collect and process personal information solely for the purpose of
            operating and improving the platform.
          </p>
          <p className="text-gray-600 mt-2">
            AirArrange does not act as a real estate agent, broker, or property
            manager and does not collect personal information for the purpose of
            negotiating or arranging leases on behalf of users.
          </p>
        </div>

        {/* What we collect */}
        <div>
          <h2 className="text-2xl font-semibold mb-4">
            Personal Information We Collect
          </h2>
          <p className="text-gray-600">
            We may collect personal information including your name, contact
            details, business information, account credentials, identity
            verification status, device and usage information, and
            communications sent through the platform.
          </p>
        </div>

        {/* How we collect */}
        <div>
          <h2 className="text-2xl font-semibold mb-4">
            How We Collect Personal Information
          </h2>
          <p className="text-gray-600">
            Personal information is collected when you create an account, list
            or enquire about a workspace, make a booking, communicate with other
            users, contact us, or interact with our website.
          </p>
        </div>

        {/* Purpose */}
        <div>
          <h2 className="text-2xl font-semibold mb-4">
            How We Use Personal Information
          </h2>
          <ul className="list-disc pl-6 text-gray-600 space-y-2">
            <li>Operating and improving the AirArrange platform</li>
            <li>Facilitating communication between users</li>
            <li>Processing bookings and payments</li>
            <li>
              Identity verification via third‑party providers such as Stripe
            </li>
            <li>Providing customer support</li>
            <li>Meeting legal and regulatory obligations</li>
          </ul>
        </div>

        {/* Payments */}
        <div>
          <h2 className="text-2xl font-semibold mb-4">Payments and Stripe</h2>
          <p className="text-gray-600">
            All payments on AirArrange are processed by Stripe, a third‑party
            payment service provider. AirArrange does not collect or store
            credit card information.
          </p>
          <p className="text-gray-600 mt-2">
            Stripe may collect and process personal information in accordance
            with its own privacy policy.
          </p>
        </div>

        {/* Messaging */}
        <div>
          <h2 className="text-2xl font-semibold mb-4">
            Messaging Between Users
          </h2>
          <p className="text-gray-600">
            Our platform allows users to communicate directly with each other.
            Messages are stored and processed to facilitate enquiries, bookings,
            safety, and dispute resolution where required.
          </p>
          <p className="text-gray-600 mt-2">
            AirArrange does not monitor messages for the purpose of negotiating
            prices or booking terms and does not participate in communications
            between users.
          </p>
        </div>

        {/* Disclosure */}
        <div>
          <h2 className="text-2xl font-semibold mb-4">
            Disclosure of Personal Information
          </h2>
          <p className="text-gray-600">
            We may disclose personal information to service providers such as
            Stripe, hosting providers, analytics services, or where required by
            law. We do not sell personal information.
          </p>
        </div>

        {/* Cookies */}
        <div>
          <h2 className="text-2xl font-semibold mb-4">Cookies and Analytics</h2>
          <p className="text-gray-600">
            AirArrange uses cookies and similar technologies to operate the
            website, analyse usage, and improve performance. This may include
            collecting information such as IP address, browser type, device
            information, and pages visited.
          </p>
          <p className="text-gray-600 mt-2">
            You may control cookie preferences through your browser settings,
            however disabling cookies may affect platform functionality.
          </p>
        </div>

        {/* Overseas */}
        <div>
          <h2 className="text-2xl font-semibold mb-4">Overseas Disclosure</h2>
          <p className="text-gray-600">
            Some service providers may store data outside Australia. We take
            reasonable steps to ensure overseas recipients comply with
            Australian privacy standards.
          </p>
        </div>

        {/* Retention */}
        <div>
          <h2 className="text-2xl font-semibold mb-4">Data Retention</h2>
          <p className="text-gray-600">
            We retain personal information only for as long as necessary to
            provide our services, meet legal obligations, resolve disputes, and
            enforce our agreements.
          </p>
          <p className="text-gray-600 mt-2">
            When personal information is no longer required, we take reasonable
            steps to delete or de‑identify it in accordance with applicable law.
          </p>
        </div>

        {/* Access */}
        <div>
          <h2 className="text-2xl font-semibold mb-4">Access and Correction</h2>
          <p className="text-gray-600">
            You may request access to or correction of your personal information
            by contacting us. Identity verification may be required.
          </p>
        </div>

        {/* Complaints */}
        <div>
          <h2 className="text-2xl font-semibold mb-4">Privacy Complaints</h2>
          <p className="text-gray-600">
            If you believe we have breached Australian privacy laws, please
            contact us first. If unresolved, you may lodge a complaint with the
            Office of the Australian Information Commissioner (OAIC).
          </p>
          <p className="text-gray-600 mt-2">
            Privacy enquiries may be directed to our Privacy Officer via the
            contact details below.
          </p>
        </div>

        {/* Contact */}
        <div className="border-t pt-8">
          <h2 className="text-2xl font-semibold mb-4">Contact Us</h2>
          <button className="secondary-button" onClick={handleContactSales}>
            Contact Us
          </button>
        </div>
      </section>
    </div>
  );
}

export default PrivacyPolicy;
